import { MmPage } from '../types/dto';
import { MmAssignmentsSummary } from '../types/dto';
import { MmCompetitorStore, MmStoreAuditorAssignment } from '../types/domain';
import { readPage } from './pagination';

/** null — «аудитора нет» или «снять аудитора», а не «данные не пришли» */
export type MmWireAuditorId = number | string | null;

export interface MmWireAssignmentStore {
  competitorCode?: string;
  name?: string | null;
  address?: string | null;
  competitorNetworkName?: string | null;
  competitorFormat?: string | null;
  priority?: number | string | null;
  storeFormatId?: number | string | null;
  storeFormatName?: string | null;
  storeRegionId?: number | string | null;
  storeRegionName?: string | null;
  storeClusterId?: number | string | null;
  storeClusterName?: string | null;
}

export interface MmWireAssignmentRow {
  competitorCode?: string;
  store?: MmWireAssignmentStore;
  auditorId?: MmWireAuditorId;
  auditorLogin?: string | null;
  auditorFullName?: string | null;
}

function pick(raw: any, names: string[]): any {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }

  for (let i = 0; i < names.length; i += 1) {
    const value = raw[names[i]];

    if (value !== undefined && value !== null) {
      return value;
    }
  }

  return undefined;
}

function readText(value: any): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  const text = String(value).trim();

  return text !== '' ? text : null;
}

/** Строковые числа бэкенд отдаёт регулярно — принимаем их */
function readNumber(value: any): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(String(value).trim());

  return isFinite(parsed) ? parsed : null;
}

function readAuditorId(value: any): MmWireAuditorId {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    return isFinite(value) ? value : null;
  }

  const text = String(value).trim();

  return text !== '' ? text : null;
}

function readStore(raw: any, competitorCode: string): MmCompetitorStore {
  return {
    competitorCode: competitorCode,
    name: readText(pick(raw, ['name', 'competitor_name', 'competitorName'])),
    address: readText(pick(raw, ['address'])),
    competitorNetworkName: readText(
      pick(raw, ['competitor_network_name', 'competitorNetworkName']),
    ),
    competitorFormat: readText(pick(raw, ['competitor_format', 'competitorFormat'])),
    priority: readNumber(pick(raw, ['priority'])),
    storeFormatId: readNumber(pick(raw, ['store_format_id', 'storeFormatId'])),
    storeFormatName: readText(pick(raw, ['store_format_name', 'storeFormatName'])),
    storeRegionId: readNumber(pick(raw, ['store_region_id', 'storeRegionId'])),
    storeRegionName: readText(pick(raw, ['store_region_name', 'storeRegionName'])),
    storeClusterId: readNumber(pick(raw, ['store_cluster_id', 'storeClusterId'])),
    storeClusterName: readText(pick(raw, ['store_cluster_name', 'storeClusterName'])),
  };
}

/** Без кода конкурента строку не показываем: он ключ строки, сохранения и выгрузки */
export function readAssignmentRow(raw: any): MmStoreAuditorAssignment | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const competitorCode = readText(pick(raw, ['competitor_code', 'competitorCode']));

  if (competitorCode === null) {
    return null;
  }

  const nested = raw.store && typeof raw.store === 'object' ? raw.store : null;

  return {
    competitorCode: competitorCode,
    store: nested !== null
      ? (nested as MmCompetitorStore)
      : readStore(raw, competitorCode),
    auditorId: readAuditorId(
      pick(raw, ['assigned_auditor_id', 'assignedAuditorId', 'auditor_id', 'auditorId']),
    ),
    auditorLogin: readText(pick(raw, ['auditor_login', 'auditorLogin'])),
    auditorFullName: readText(
      pick(raw, ['auditor_full_name', 'auditorFullName', 'auditor_fio', 'fio']),
    ),
  };
}

export function readAssignmentsPage(data: any): MmPage<MmStoreAuditorAssignment> {
  const page = readPage<any>(data);
  const rows: MmStoreAuditorAssignment[] = [];

  for (let i = 0; i < page.content.length; i += 1) {
    const row = readAssignmentRow(page.content[i]);

    if (row !== null) {
      rows.push(row);
    }
  }

  return {
    content: rows,
    totalElements: page.totalElements,
    totalKnown: page.totalKnown,
    hasMore: page.hasMore,
  };
}

export function readAssignmentsSummary(data: any): MmAssignmentsSummary {
  const total = readNumber(pick(data, ['total', 'total_count', 'totalCount']));
  const withoutAuditor = readNumber(
    pick(data, ['without_auditor', 'withoutAuditor', 'without_auditor_count']),
  );

  return {
    total: total !== null ? total : 0,
    withoutAuditor: withoutAuditor !== null ? withoutAuditor : 0,
  };
}

export function buildAssignmentsFilterBody(
  tradingNetworkId: number,
  filters: { [key: string]: any },
): { [key: string]: any } {
  const source: { [key: string]: any } = filters ? filters : {};

  return {
    tradingNetworkId: tradingNetworkId,
    formats: Array.isArray(source.formats) ? source.formats : [],
    regions: Array.isArray(source.regions) ? source.regions : [],
    clusters: Array.isArray(source.clusters) ? source.clusters : [],
    q: readText(source.q),
    competitorCodes: Array.isArray(source.competitorCodes) ? source.competitorCodes : [],
    auditorLogins: Array.isArray(source.auditorLogins) ? source.auditorLogins : [],
    onlyWithoutAuditor: source.onlyWithoutAuditor === true,
  };
}

/** Имена в том же виде, что у бэкенда: незнакомое он молча заменит сортировкой по коду конкурента */
export const MM_ASSIGNMENT_SORT_FIELDS: { [key: string]: string } = {
  storeFormatName: 'storeFormatName',
  storeRegionName: 'storeRegionName',
  storeClusterName: 'storeClusterName',
  competitorCode: 'competitorCode',
  name: 'name',
  address: 'address',
  priority: 'priority',
  auditorLogin: 'auditorLogin',
};
